import React, { useState, useEffect } from "react";
import RichTextEditor from "./RichTextEditor";
import { getLabelCategories, handleLabel } from "./policySlide";
// npm install react-quill --legacy-peer-deps
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import styles from "./policy.module.scss";
import {
  fetchProjects,
  selectProjectById,
  selectProjectIds
} from "../projects/projectsSlice";

export function PolicyForm() {
  const { id } = useParams();
  const history = useHistory();
  const sInitialData = {};
  const [name, setName] = useState(sInitialData.name || "");
  const [code, setCode] = useState(sInitialData.code || "");
  const [definition, setDefinition] = useState(sInitialData.definition || "");
  const [categoryId, setCategoryId] = useState(sInitialData.categoryId || "");
  const [projectId, setProjectId] = useState(sInitialData.projectId || "");
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const dispatch = useDispatch();
  const allCategories = useSelector((state) => state.policy?.categories ?? []);

  const orderedProjectIds = useSelector(selectProjectIds);
  const projectStatus = useSelector(state => state.project?.status ?? "idle");
  useEffect(() => {
    if (projectStatus === "idle") {
      dispatch(fetchProjects());
    }
  }, [projectStatus, dispatch]);
  const projects = useSelector((state) => orderedProjectIds.map(id => selectProjectById(state, id)));


  useEffect(() => {
    dispatch(getLabelCategories());
  }, []);

  // Load policy details if ID exists
  useEffect(() => {
    if (id) {
      loadPolicyDetails();
    }
  }, [id]);

  const loadPolicyDetails = async () => {
    setLoading(true);
    try {
      const result = await dispatch(handleLabel({
        action: 'get',
        id: parseInt(id)
      }));

      if (result.type === 'policy/handleLabel/fulfilled') {
        const response = result.payload;

        if (response?.existed) {
          const policy = response.existed;

          // Set form values
          setName(policy.name || "");
          setCode(policy.code || "");
          setDefinition(policy.definition || "");
          // Handle category_id - convert to string for single select
          let categoryId = policy.category_id;
          if (Array.isArray(categoryId)) {
            setCategoryId(categoryId.length > 0 ? String(categoryId[0]) : "");
          } else if (categoryId) {
            setCategoryId(String(categoryId));
          } else {
            setCategoryId("");
          }
          // Handle project_id
          let _projectId = policy.project_id;
          if (_projectId) {
            setProjectId(String(_projectId));
          } else {
            setProjectId("");
          }
        }
      }
    } catch (error) {
      // Handle error silently or add user-friendly error handling
    } finally {
      setLoading(false);
    }
  };

  // Handle single select change
  const handleCategoryChange = (e) => {
    setCategoryId(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const labelData = {
      name,
      code,
      definition,
      category_id: categoryId !== '' && categoryId !== null ? parseInt(categoryId) : null,
      project_id: projectId !== '' && projectId !== null ? parseInt(projectId) : null,
      action: id !== undefined && id !== '' ? 'update' : 'create'
    };
    if (id !== undefined && id !== '') {
      labelData.id = parseInt(id);
    }
    try {
      const result = await dispatch(handleLabel(labelData));
      if (result.type === 'policy/handleLabel/fulfilled') {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          history.push('/policy');
        }, 2000);
      }
    } catch (error) {
      // You can add error handling here (show toast, alert, etc.)
    }
  };

  // Delete Policy Function
  const handleDeletePolicy = async () => {
    try {
      setLoading(true);

      const deleteData = {
        action: 'delete',
        id: parseInt(id)
      };

      const result = await dispatch(handleLabel(deleteData));

      if (result.type === 'policy/handleLabel/fulfilled') {
        setShowDeleteConfirm(false);
        // Navigate back to policy list on success
        history.push('/policy');
      }
      // Keep dialog open on error if request fails
    } catch (error) {
      // Keep dialog open on error
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  if (loading) {
    return (
      <div className={styles.policyFormContainer}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading policy details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.policyFormContainer}>
      <div className={styles.policyFormCard}>
        <div className={styles.formHeader}>
          <h2 className={styles.formTitle}>
            {id ? 'Update Policy' : 'Create New Policy'}
          </h2>
          <p className={styles.formSubtitle}>
            {id ? 'Modify the existing policy details below.' : 'Fill in the details to create a new policy.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.policyForm}>
          <div className={styles.formSection}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Policy Name <span className={styles.required}>*</span>
              </label>
              <input
                required
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={styles.formInput}
                placeholder="Enter policy name"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Policy Code <span className={styles.required}>*</span>
              </label>
              <input
                required
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className={styles.formInput}
                placeholder="Enter policy code"
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Definition
              </label>
              <RichTextEditor
                value={definition}
                onChange={setDefinition}
                placeholder="Enter policy definition..."
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Project <span className={styles.required}>*</span>
              </label>
              <select
                value={projectId}
                onChange={e => setProjectId(e.target.value)}
                className={styles.formSelect}
                required
                disabled={!!id}
              >
                <option value="">Select a project</option>
                {Array.isArray(projects) && projects.map((proj) => (
                  <option key={proj.id} value={proj.id} className={styles.selectOption}>
                    {proj.name}
                  </option>
                ))}
              </select>
              <small className={styles.formHelp}>
                {id ? "Project cannot be changed after creation" : "Choose a project for this policy"}
              </small>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Category
              </label>
              <select
                value={categoryId}
                onChange={handleCategoryChange}
                className={styles.formSelect}
              >
                <option value="">Select a category</option>
                {Array.isArray(allCategories) && allCategories.map((cat) => (
                  <option key={cat.id} value={cat.id} className={styles.selectOption}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <small className={styles.formHelp}>
                Choose a category for this policy
              </small>
            </div>
          </div>

          {showSuccess && (
            <div className={`${styles.formGroup} text-center `}>
              <label className={`text-success ${styles.formLabel} ${styles.successBackground}`} >
                Policy saved successfully!
              </label>  
            </div>
          )}
          
          <div className={styles.formActions}>
            <div className={styles.leftActions}>
              {id && (
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className={styles.deleteButton}
                >
                  Delete Policy
                </button>
              )}
            </div>
            <div className={styles.rightActions}>
              <button
                type="button"
                onClick={() => history.push('/policy')}
                className={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={loading}
              >
                {loading ? 'Saving...' : (id ? 'Update Policy' : 'Create Policy')}
              </button>
            </div>
          </div>
        </form>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className={styles.deleteConfirmOverlay}>
            <div className={styles.deleteConfirmDialog}>
              <div className={styles.deleteConfirmHeader}>
                <h3>Confirm Delete</h3>
              </div>
              <div className={styles.deleteConfirmBody}>
                <p>Are you sure you want to delete this policy?</p>
                <p className={styles.deleteWarning}>
                  This action cannot be undone.
                </p>
              </div>
              <div className={styles.deleteConfirmActions}>
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  className={styles.deleteConfirmCancelButton}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeletePolicy}
                  className={styles.deleteConfirmDeleteButton}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete Policy'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PolicyForm;